// repo/primary-care-connect/frontend/src/pages/Portal/SuperAdmin/Clinics/CreateBasicClinicPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CreateBasicClinicPage.css";

/**
 * Super Admin — Create Basic Clinic
 *
 * Purpose:
 * - Create an approved clinic record from the Super Admin portal
 * - Require Address Line 1 to be selected from autocomplete
 * - Require phone as a 10-digit formatted number
 * - Keep extension as a separate optional digits-only field
 */

type ApiErrorShape = { error?: string; fields?: Record<string, string> } | null;

type CityOption = { CityID: number; Name: string };
type CitiesResponse = { cities?: CityOption[] } | CityOption[];

type AddressSuggestion = {
  id: string;
  label: string; // full pretty label for dropdown display
  addressLine1?: string | null; // server should return street-only line (e.g., "3265 Carding Mill Trail")
  postalCode?: string | null;
  city?: string | null;
  province?: string | null;
};

type AddressSuggestResponse = { suggestions?: AddressSuggestion[]; error?: string };

const PROVINCES: { code: string; label: string }[] = [
  { code: "ON", label: "Ontario (ON)" },
  { code: "BC", label: "British Columbia (BC)" },
  { code: "AB", label: "Alberta (AB)" },
  { code: "MB", label: "Manitoba (MB)" },
  { code: "NB", label: "New Brunswick (NB)" },
  { code: "NL", label: "Newfoundland and Labrador (NL)" },
  { code: "NS", label: "Nova Scotia (NS)" },
  { code: "NT", label: "Northwest Territories (NT)" },
  { code: "NU", label: "Nunavut (NU)" },
  { code: "PE", label: "Prince Edward Island (PE)" },
  { code: "QC", label: "Quebec (QC)" },
  { code: "SK", label: "Saskatchewan (SK)" },
  { code: "YT", label: "Yukon (YT)" },
];

// --------------------
// Utils (small + local)
// --------------------
function asString(v: any): string {
  if (v === null || v === undefined) return "";
  return String(v);
}
function trim(v: any): string {
  return asString(v).trim();
}
function normLower(v: any): string {
  return trim(v).toLowerCase();
}

function formatPostalInput(raw: string): string {
  const s = asString(raw).toUpperCase();
  const cleaned = s.replace(/[^A-Z0-9]/g, "").slice(0, 6);
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
}
function normalizePostalForApi(displayVal: string): string {
  return asString(displayVal).toUpperCase().replace(/\s+/g, "");
}
function isPostalComplete(displayVal: string): boolean {
  return /^[A-Z]\d[A-Z]\s\d[A-Z]\d$/.test(
    asString(displayVal).toUpperCase().trim()
  );
}

function formatPhoneInput(raw: string): string {
  const s = asString(raw);
  const digits = s.replace(/\D/g, "").slice(0, 10);

  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);

  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${a}`;
  if (digits.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}
function isPhoneComplete(displayVal: string): boolean {
  return /^\(\d{3}\)\s\d{3}-\d{4}$/.test(trim(displayVal));
}

function normalizeExtensionInput(raw: string): string {
  const s = trim(raw);
  if (!s) return "";
  return s.replace(/\D/g, "").slice(0, 8);
}
function isExtensionValid(raw: string): boolean {
  const s = trim(raw);
  if (!s) return true;
  return /^\d{1,8}$/.test(s);
}

// --------------------
// Small presentational field wrapper
// --------------------
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="sa-clinics__field">
      <div className="sa-clinics__label">{label}</div>
      {children}
    </div>
  );
}

// --------------------
// Address field component (outside page to avoid focus loss)
// --------------------
function AddressLine1Field(props: {
  addressWrapRef: React.RefObject<HTMLDivElement | null>;
  address1: string;
  setAddress1: (v: string) => void;

  addrSuggestions: AddressSuggestion[];
  addrSuggestOpen: boolean;
  setAddrSuggestOpen: (v: boolean) => void;
  addrSuggestLoading: boolean;
  addrSuggestErr: string | null;

  applySuggestion: (s: AddressSuggestion) => void;

  fieldError?: string | null;
  addrSelectedId: string | null;

  suppressOpenOnceRef: React.MutableRefObject<boolean>;
}) {
  const {
    addressWrapRef,
    address1,
    setAddress1,
    addrSuggestions,
    addrSuggestOpen,
    setAddrSuggestOpen,
    addrSuggestLoading,
    addrSuggestErr,
    applySuggestion,
    fieldError,
    addrSelectedId,
    suppressOpenOnceRef,
  } = props;

  return (
    <Field label="Address Line 1 *">
      <div ref={addressWrapRef} style={{ position: "relative" }}>
        <input
          type="text"
          value={address1}
          onChange={(e) => {
            setAddress1(e.target.value);

            if (suppressOpenOnceRef.current) {
              suppressOpenOnceRef.current = false;
            } else {
              setAddrSuggestOpen(true);
            }
          }}
          onFocus={() => {
            if (addrSuggestions.length || addrSuggestErr) setAddrSuggestOpen(true);
          }}
          className="sa-clinics__input"
          placeholder="Start typing, then select from dropdown…"
          autoComplete="off"
        />

        {addrSuggestOpen ? (
          <div
            style={{
              position: "absolute",
              zIndex: 50,
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              boxShadow: "0 14px 30px rgba(15, 23, 42, 0.12)",
              overflow: "hidden",
            }}
          >
            {addrSuggestLoading ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "#64748b",
                  fontWeight: 800,
                }}
              >
                Searching…
              </div>
            ) : addrSuggestErr ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "#991b1b",
                  fontWeight: 800,
                }}
              >
                {addrSuggestErr}
              </div>
            ) : addrSuggestions.length === 0 ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "#64748b",
                  fontWeight: 800,
                }}
              >
                Type at least 4 characters to see suggestions.
              </div>
            ) : (
              addrSuggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    applySuggestion(s);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 12px",
                    border: 0,
                    background: "#ffffff",
                    cursor: "pointer",
                    fontSize: 13,
                    color: "#0f172a",
                    fontWeight: 800,
                    borderTop: "1px solid #eef2f7",
                  }}
                  title={s.label}
                >
                  {s.label}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      {fieldError ? (
        <div className="sa-clinics__hint" style={{ color: "#b00020" }}>
          {fieldError}
        </div>
      ) : addrSelectedId ? (
        <div className="sa-clinics__hint">Address selected ✓</div>
      ) : null}
    </Field>
  );
}

// --------------------
// Page
// --------------------
export default function CreateBasicClinicPage() {
  const nav = useNavigate();

  const [step, setStep] = useState<"edit" | "review">("edit");
  const [busy, setBusy] = useState(false);

  const [err, setErr] = useState<string | null>(null);
  const [fieldErrs, setFieldErrs] = useState<Record<string, string> | null>(null);
  const [dupClinicId, setDupClinicId] = useState<string | null>(null);

  // Cities
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesErr, setCitiesErr] = useState<string | null>(null);
  const [cities, setCities] = useState<CityOption[]>([]);

  // Address autocomplete
  const addressWrapRef = useRef<HTMLDivElement | null>(null);
  const [addrSuggestions, setAddrSuggestions] = useState<AddressSuggestion[]>([]);
  const [addrSuggestOpen, setAddrSuggestOpen] = useState(false);
  const [addrSuggestLoading, setAddrSuggestLoading] = useState(false);
  const [addrSuggestErr, setAddrSuggestErr] = useState<string | null>(null);

  // Must-select gate
  const [addrSelectedId, setAddrSelectedId] = useState<string | null>(null);
  const [addrSelectedLabel, setAddrSelectedLabel] = useState<string | null>(null);

  // Anti-race flags
  const suppressSuggestOnceRef = useRef(false);
  const suppressOpenOnceRef = useRef(false);

  // Form fields
  const [clinicName, setClinicName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [postal, setPostal] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneExtension, setPhoneExtension] = useState("");
  const [province, setProvince] = useState("ON");
  const [cityId, setCityId] = useState<number | "">("");

  const goBackToList = () => nav("/portal/super-admin/clinics");
  const openClinic = (clinicIdToOpen: number | string) => {
    nav(`/portal/super-admin/clinics/${encodeURIComponent(String(clinicIdToOpen))}`);
  };

  const reset = () => {
    setErr(null);
    setFieldErrs(null);
    setDupClinicId(null);
    setStep("edit");

    setClinicName("");
    setAddress1("");
    setAddress2("");
    setPostal("");
    setPhone("");
    setPhoneExtension("");
    setProvince("ON");
    setCityId("");

    setAddrSuggestions([]);
    setAddrSuggestOpen(false);
    setAddrSuggestLoading(false);
    setAddrSuggestErr(null);
    setAddrSelectedId(null);
    setAddrSelectedLabel(null);

    suppressSuggestOnceRef.current = false;
    suppressOpenOnceRef.current = false;
  };

  // Close address dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      const wrap = addressWrapRef.current;
      if (!wrap) return;
      if (!wrap.contains(ev.target as any)) setAddrSuggestOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Load cities
  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      setCitiesLoading(true);
      setCitiesErr(null);

      try {
        const res = await fetch("/api/cities", { credentials: "include" });
        if (!res.ok) {
          let msg = `Failed to load cities (${res.status})`;
          try {
            const j = (await res.json()) as ApiErrorShape;
            if (j?.error) msg = j.error;
          } catch {}
          throw new Error(msg);
        }

        const data = (await res.json()) as CitiesResponse;

        let list: CityOption[] = [];
        if (Array.isArray(data)) list = data as CityOption[];
        else if (Array.isArray((data as any)?.cities))
          list = (data as any).cities as CityOption[];

        const normalized: CityOption[] = list
          .map((c: any) => ({
            CityID: Number(c.CityID ?? c.cityId ?? c.id),
            Name: String(c.Name ?? c.name ?? ""),
          }))
          .filter((c) => Number.isFinite(c.CityID) && c.Name.trim().length > 0)
          .sort((a, b) => a.Name.localeCompare(b.Name));

        if (!cancelled) setCities(normalized);
      } catch (e: any) {
        if (!cancelled) setCitiesErr(e?.message || "Failed to load cities");
      } finally {
        if (!cancelled) setCitiesLoading(false);
      }
    };

    loadCities();
    return () => {
      cancelled = true;
    };
  }, []);

  // Address suggestions (debounced)
  useEffect(() => {
    let cancelled = false;
    const q = trim(address1);

    if (suppressSuggestOnceRef.current) {
      suppressSuggestOnceRef.current = false;
      return;
    }

    if (addrSelectedId && addrSelectedLabel && q !== addrSelectedLabel) {
      setAddrSelectedId(null);
      setAddrSelectedLabel(null);
    }

    if (q.length < 4) {
      setAddrSuggestions([]);
      setAddrSuggestErr(null);
      setAddrSuggestLoading(false);
      setAddrSuggestOpen(false);
      return;
    }

    const t = window.setTimeout(async () => {
      setAddrSuggestLoading(true);
      setAddrSuggestErr(null);

      try {
        const url =
          `/api/mapbox-address-suggest?country=CA&query=` + encodeURIComponent(q);

        const res = await fetch(url, { credentials: "include" });
        const data = (await res.json().catch(() => null)) as AddressSuggestResponse | null;

        if (!res.ok) {
          const msg =
            (data as any)?.error ||
            (data as any)?.message ||
            `Address lookup failed (${res.status})`;
          throw new Error(msg);
        }

        const list = Array.isArray((data as any)?.suggestions)
          ? ((data as any).suggestions as AddressSuggestion[])
          : [];

        const normalized = list
          .map((s: any) => ({
            id: String(s.id ?? ""),
            label: String(s.label ?? ""),
            addressLine1: s.addressLine1 ?? null,
            postalCode: s.postalCode ?? null,
            city: s.city ?? null,
            province: s.province ?? null,
          }))
          .filter((s) => s.id && s.label)
          .slice(0, 8);

        if (!cancelled) {
          setAddrSuggestions(normalized);
          setAddrSuggestOpen(true);
        }
      } catch (e: any) {
        if (!cancelled) {
          setAddrSuggestions([]);
          setAddrSuggestErr(e?.message || "Address lookup failed");
          setAddrSuggestOpen(true);
        }
      } finally {
        if (!cancelled) setAddrSuggestLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address1]);

  const reviewCityName = useMemo(() => {
    const id = Number(cityId);
    if (!Number.isFinite(id)) return "—";
    const found = cities.find((c) => c.CityID === id);
    return found?.Name || "—";
  }, [cityId, cities]);

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};

    if (!trim(clinicName)) e.clinicName = "Clinic Name is required";

    if (!trim(address1)) e.addressLine1 = "Address Line 1 is required";
    else if (!addrSelectedId)
      e.addressLine1 = "Please select an address from the dropdown options.";

    if (!trim(postal)) e.postalCode = "Postal Code is required";
    else if (!isPostalComplete(postal))
      e.postalCode = "Postal Code must be in format X0X 0X0";

    if (!trim(phone)) e.phone = "Phone Number is required";
    else if (!isPhoneComplete(phone))
      e.phone = "Phone Number must be in format (905) 000-0000";

    if (trim(phone) && /(ext|extension|\bx\b)/i.test(phone)) {
      e.phone = "Please enter the extension in the separate Extension field";
    }

    if (!isExtensionValid(phoneExtension)) {
      e.phoneExtension = "Extension must be digits only (1–8 digits)";
    }

    if (!trim(province)) e.province = "Province is required";
    if (!cityId) e.cityId = "City is required";

    return e;
  };

  const goReview = () => {
    setErr(null);
    setFieldErrs(null);
    setDupClinicId(null);

    const e = validate();
    if (Object.keys(e).length) {
      setFieldErrs(e);
      return;
    }

    setStep("review");
  };

  const goEdit = () => {
    setErr(null);
    setStep("edit");
  };

  const applySuggestion = (s: AddressSuggestion) => {
    const line1 = trim(s.addressLine1) || trim(s.label);

    suppressSuggestOnceRef.current = true;
    suppressOpenOnceRef.current = true;

    setAddrSelectedId(s.id);
    setAddrSelectedLabel(line1);
    setAddress1(line1);

    const pc = trim(s.postalCode);
    if (pc) setPostal(formatPostalInput(pc));

    const prov = trim(s.province).toUpperCase();
    if (prov && prov.length === 2) setProvince(prov);

    const cityName = trim(s.city);
    if (cityName) {
      const match = cities.find((c) => normLower(c.Name) === normLower(cityName));
      if (match) setCityId(match.CityID);
    }

    setAddrSuggestOpen(false);
    setAddrSuggestions([]);
    setAddrSuggestErr(null);
  };

  const submit = async () => {
    setBusy(true);
    setErr(null);
    setFieldErrs(null);
    setDupClinicId(null);

    try {
      const e = validate();
      if (Object.keys(e).length) {
        setFieldErrs(e);
        setStep("edit");
        return;
      }

      const payload = {
        clinicName: trim(clinicName),
        addressLine1: trim(address1),
        addressLine2: trim(address2) || null,
        postalCode: normalizePostalForApi(postal),
        phone: trim(phone),
        phoneExtension: trim(phoneExtension) ? trim(phoneExtension) : null,
        province: trim(province),
        cityId: Number(cityId),
      };

      const res = await fetch("/api/superadmin-clinic-create-basic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = (await res.json().catch(() => null)) as any;

      if (!res.ok) {
        const fields = data?.fields;
        if (fields && typeof fields === "object") {
          setFieldErrs(fields as Record<string, string>);
          setStep("edit");
          setErr(data?.error || `Create failed (${res.status})`);
          return;
        }

        const code = String(data?.code || "").trim();
        if (res.status === 409 && code === "CLINIC_ALREADY_EXISTS") {
          const existingId =
            data?.clinicId ?? data?.ClinicID ?? data?.clinicID ?? null;
          if (existingId !== null && existingId !== undefined)
            setDupClinicId(String(existingId));
          setErr(
            data?.error ||
              "A clinic with the same name and address already exists."
          );
          return;
        }

        setErr(data?.error || data?.message || `Create failed (${res.status})`);
        return;
      }

      const createdClinicId =
        data?.clinic?.ClinicID ?? data?.clinic?.clinicId ?? null;
      if (!createdClinicId) {
        setErr("Clinic created but response was missing ClinicID.");
        return;
      }

      openClinic(createdClinicId);
    } catch (e: any) {
      setErr(e?.message || "Failed to create clinic");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sa-clinics">
      <div className="sa-clinics__header">
        <div className="sa-clinics__titleWrap">
          <h1 className="sa-clinics__title">Create Basic Clinic</h1>
          <p className="sa-clinics__subtitle">
            Create an approved clinic record with address verification via
            geocoding.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={goBackToList}
            className="sa-clinics__rowBtn"
            disabled={busy}
          >
            Back to clinics
          </button>
        </div>
      </div>

      <div className="sa-clinics__card" style={{ marginBottom: 16 }}>
        <div className="sa-clinics__cardHeader">
          <div className="sa-clinics__cardTitle">
            {step === "edit" ? "Clinic details" : "Review"}
          </div>
        </div>

        {citiesErr ? <div className="sa-clinics__error">{citiesErr}</div> : null}

        {err ? (
          <div className="sa-clinics__error">
            {err}
            {dupClinicId ? (
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="sa-clinics__rowBtn"
                  onClick={() => openClinic(dupClinicId)}
                  disabled={busy}
                  title="Open the existing clinic record"
                >
                  Open existing clinic (ClinicID: {dupClinicId})
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {step === "edit" ? (
          <div style={{ padding: 14 }}>
            <div className="sa-clinics__filters" style={{ marginTop: 0 }}>
              <Field label="Clinic Name *">
                <input
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="sa-clinics__input"
                />
                {fieldErrs?.clinicName ? (
                  <div className="sa-clinics__hint" style={{ color: "#b00020" }}>
                    {fieldErrs.clinicName}
                  </div>
                ) : null}
              </Field>

              <AddressLine1Field
                addressWrapRef={addressWrapRef}
                address1={address1}
                setAddress1={setAddress1}
                addrSuggestions={addrSuggestions}
                addrSuggestOpen={addrSuggestOpen}
                setAddrSuggestOpen={setAddrSuggestOpen}
                addrSuggestLoading={addrSuggestLoading}
                addrSuggestErr={addrSuggestErr}
                applySuggestion={applySuggestion}
                fieldError={fieldErrs?.addressLine1 || null}
                addrSelectedId={addrSelectedId}
                suppressOpenOnceRef={suppressOpenOnceRef}
              />

              <Field label="Address Line 2 (optional)">
                <input
                  type="text"
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                  className="sa-clinics__input"
                  placeholder="Suite / Unit (optional)"
                />
              </Field>

              <Field label="Postal Code *">
                <input
                  type="text"
                  value={postal}
                  onChange={(e) => setPostal(formatPostalInput(e.target.value))}
                  className="sa-clinics__input"
                  placeholder="X0X 0X0"
                  inputMode="text"
                  autoComplete="postal-code"
                />
                {fieldErrs?.postalCode ? (
                  <div className="sa-clinics__hint" style={{ color: "#b00020" }}>
                    {fieldErrs.postalCode}
                  </div>
                ) : null}
              </Field>

              <Field label="Phone Number *">
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                  className="sa-clinics__input"
                  placeholder="(905) 000-0000"
                  inputMode="tel"
                  autoComplete="tel"
                />
                {fieldErrs?.phone ? (
                  <div className="sa-clinics__hint" style={{ color: "#b00020" }}>
                    {fieldErrs.phone}
                  </div>
                ) : null}
              </Field>

              <Field label="Extension (optional)">
                <input
                  type="text"
                  value={phoneExtension}
                  onChange={(e) =>
                    setPhoneExtension(normalizeExtensionInput(e.target.value))
                  }
                  className="sa-clinics__input"
                  placeholder="123"
                  inputMode="numeric"
                  autoComplete="off"
                />
                {fieldErrs?.phoneExtension ? (
                  <div className="sa-clinics__hint" style={{ color: "#b00020" }}>
                    {fieldErrs.phoneExtension}
                  </div>
                ) : null}
              </Field>

              <Field label="Province *">
                <select
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  className="sa-clinics__input"
                >
                  {PROVINCES.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.label}
                    </option>
                  ))}
                </select>
                {fieldErrs?.province ? (
                  <div className="sa-clinics__hint" style={{ color: "#b00020" }}>
                    {fieldErrs.province}
                  </div>
                ) : null}
              </Field>

              <Field label="City *">
                <select
                  value={cityId === "" ? "" : String(cityId)}
                  onChange={(e) =>
                    setCityId(e.target.value ? Number(e.target.value) : "")
                  }
                  className="sa-clinics__input"
                  disabled={citiesLoading || !!citiesErr}
                >
                  <option value="">
                    {citiesLoading ? "Loading…" : "Select a city"}
                  </option>
                  {cities.map((c) => (
                    <option key={c.CityID} value={String(c.CityID)}>
                      {c.Name}
                    </option>
                  ))}
                </select>
                {fieldErrs?.cityId ? (
                  <div className="sa-clinics__hint" style={{ color: "#b00020" }}>
                    {fieldErrs.cityId}
                  </div>
                ) : null}
              </Field>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={reset}
                className="sa-clinics__rowBtn"
                disabled={busy}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={goReview}
                className="sa-clinics__rowBtn"
                disabled={busy}
              >
                Review
              </button>
            </div>

            <div className="sa-clinics__hint" style={{ marginTop: 10 }}>
              Address must be selected from the dropdown; creation is blocked
              unless the address can be verified by geocoding.
            </div>
          </div>
        ) : (
          <div style={{ padding: 14 }}>
            <div className="sa-clinics__hint" style={{ marginBottom: 10 }}>
              Review the clinic details before creating the record.
            </div>

            <table className="sa-clinics__table" style={{ marginBottom: 12 }}>
              <tbody>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td" style={{ width: 220 }}>
                    Clinic Name
                  </td>
                  <td className="sa-clinics__td">{trim(clinicName) || "—"}</td>
                </tr>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td">Address Line 1</td>
                  <td className="sa-clinics__td">{trim(address1) || "—"}</td>
                </tr>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td">Address Line 2</td>
                  <td className="sa-clinics__td">{trim(address2) || "—"}</td>
                </tr>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td">City</td>
                  <td className="sa-clinics__td">{reviewCityName}</td>
                </tr>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td">Province</td>
                  <td className="sa-clinics__td">{trim(province) || "—"}</td>
                </tr>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td">Postal Code</td>
                  <td className="sa-clinics__td">
                    {formatPostalInput(postal) || "—"}
                  </td>
                </tr>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td">Phone</td>
                  <td className="sa-clinics__td">{trim(phone) || "—"}</td>
                </tr>
                <tr className="sa-clinics__tr">
                  <td className="sa-clinics__td">Extension</td>
                  <td className="sa-clinics__td">
                    {trim(phoneExtension) || "—"}
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={goEdit}
                className="sa-clinics__rowBtn"
                disabled={busy}
              >
                Back
              </button>
              <button
                type="button"
                onClick={submit}
                className="sa-clinics__rowBtn"
                disabled={busy}
              >
                {busy ? "Creating…" : "Create clinic"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}